import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileDown, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError, exportUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import InfoTip from "../components/InfoTip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function InfoRow({ label, value, mono, tip }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {label} {tip && <InfoTip text={tip} testid={`tip-${label.toLowerCase().replace(/\s/g, "-")}`} />}
      </span>
      <span className={`text-sm text-right ${mono ? "mono" : ""}`}>{value || "-"}</span>
    </div>
  );
}

export default function DriverDetail() {
  const { id } = useParams();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    API.get(`/drivers/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [id]);

  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 p-6 text-red-700 dark:text-red-300" data-testid="driver-error">{error}</div>;
  }
  if (!data) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>;
  }

  const p = data.person;

  return (
    <div className="space-y-6" data-testid="driver-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild data-testid="back-to-drivers">
            <Link to="/conducteurs"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <nav className="text-xs text-muted-foreground mb-1"><Link to="/conducteurs" className="hover:underline">Conducteurs</Link> / {p.nom} {p.prenom}</nav>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold" data-testid="driver-name">{p.nom} {p.prenom}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge value={p.statut} testid="driver-status" />
          {hasPerm("EXPORT_DATA") && (
            <Button variant="outline" size="sm" asChild data-testid="driver-export-pdf">
              <a href={exportUrl(`/drivers/${id}/fiche.pdf`)} target="_blank" rel="noreferrer">
                <FileDown className="h-4 w-4 mr-1" /> Fiche PDF
              </a>
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="driver-identity">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#009E60]/10 text-[#009E60]">
                <UserIcon className="h-5 w-5" />
              </span>
              Identite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <InfoRow label="Nom" value={p.nom} />
            <InfoRow label="Prenom" value={p.prenom} />
            <InfoRow label="Date de naissance" value={p.date_naissance} />
            <InfoRow label="Lieu de naissance" value={p.lieu_naissance} />
            <InfoRow label="Sexe" value={p.sexe} />
            <InfoRow label="Nationalite" value={p.nationalite} />
          </CardContent>
        </Card>

        <Card data-testid="driver-contact">
          <CardHeader><CardTitle className="text-base font-heading">Coordonnees</CardTitle></CardHeader>
          <CardContent>
            <InfoRow label="Telephone" value={p.telephone} mono />
            <InfoRow label="Email" value={p.email} />
            <InfoRow label="Adresse" value={p.adresse} />
            <InfoRow label="Ville" value={p.ville} />
            <InfoRow label="Province" value={p.province} />
          </CardContent>
        </Card>

        <Card data-testid="driver-professional">
          <CardHeader><CardTitle className="text-base font-heading">Informations professionnelles</CardTitle></CardHeader>
          <CardContent>
            <InfoRow label="Profession" value={p.profession} />
            <InfoRow label="Fonction" value={p.fonction} />
            <InfoRow label="Structure" value={data.organization?.nom} />
            <InfoRow label="Matricule" value={p.matricule} mono />
          </CardContent>
        </Card>

        <Card data-testid="driver-licenses">
          <CardHeader><CardTitle className="text-base font-heading">Permis de conduire</CardTitle></CardHeader>
          <CardContent>
            {data.licenses.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun permis enregistre.</p>
            ) : data.licenses.map((l) => (
              <div key={l.id} className="mb-3 rounded-lg border p-3" data-testid={`license-${l.id}`}>
                <div className="flex items-center justify-between">
                  <span className="mono font-semibold text-sm">{l.numero}</span>
                  <StatusBadge value={l.statut} />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <span>Categorie : <b className="text-foreground">{l.categorie}</b></span>
                  <span>Delivre : {l.date_delivrance || "-"}</span>
                  <span>Expire : {l.date_expiration || "-"}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="driver-vehicles">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            Vehicules associes
            <InfoTip testid="tip-vin-detail" text="Le VIN est le numero d'identification unique du vehicule (numero de chassis)." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.vehicles.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun vehicule associe.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-accent/60 text-left">
                  <tr>{["Immatriculation", "Marque", "Modele", "Type", "VIN", "Periode", "Statut", ""].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {data.vehicles.map((v, i) => (
                    <tr key={v.id} className="border-t hover:bg-accent/40" data-testid={`driver-vehicle-${i}`}>
                      <td className="px-3 py-2.5 mono text-xs font-semibold">{v.immatriculation}</td>
                      <td className="px-3 py-2.5">{v.marque}</td>
                      <td className="px-3 py-2.5">{v.modele}</td>
                      <td className="px-3 py-2.5 text-xs">{v.type}</td>
                      <td className="px-3 py-2.5 mono text-xs">{v.vin}</td>
                      <td className="px-3 py-2.5 text-xs">{v.assignment_start}{v.assignment_end ? ` au ${v.assignment_end}` : " (actuel)"}</td>
                      <td className="px-3 py-2.5"><StatusBadge value={v.statut} /></td>
                      <td className="px-3 py-2.5">
                        <Button asChild variant="ghost" size="sm"><Link to={`/vehicules/${v.id}`} data-testid={`driver-vehicle-view-${i}`}>Consulter</Link></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
